"use client";

import { Footer, Header, Notification } from "@/components";
import { ImpressionTrackingErrorBoundary } from "@/components/ErrorBoundary/ImpressionTrackingErrorBoundary";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { DialogProvider } from "@/contexts/DialogContext";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { GlobalImpressionProvider } from "@/contexts/GlobalImpressionContext";
import { GoogleAuthProvider } from "@/contexts/GoogleAuthProvider";
import { HeaderProvider, useHeaderRef } from "@/contexts/HeaderContext";
import { PostHogProvider } from "@/contexts/PostHogContext";
import { PriceTrackingProvider } from "@/contexts/PriceTrackingContext";
import theme from "@/theme";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v15-appRouter";
import { ThemeProvider } from "@mui/material/styles";
import { Suspense } from "react";

interface ProvidersProps {
  children: React.ReactNode;
}

const HeaderWithRef = () => {
  const { headerRef } = useHeaderRef();
  return <Header ref={headerRef} />;
};

const Providers = ({ children }: ProvidersProps) => (
  <AppRouterCacheProvider>
    <ThemeProvider theme={theme}>
      <PostHogProvider>
        <GoogleAuthProvider>
          <AuthProvider>
            <CurrencyProvider>
              <FavoritesProvider>
                <PriceTrackingProvider>
                  <DialogProvider>
                    <HeaderProvider>
                      <ImpressionTrackingErrorBoundary
                        onError={(error, errorInfo) => {
                          console.warn('Impression tracking system error:', {
                            error: error.message,
                            componentStack: errorInfo.componentStack,
                          });
                        }}
                      >
                        <GlobalImpressionProvider>
                          <HeaderWithRef />
                          <Suspense fallback={null}>
                            <Notification />
                          </Suspense>
                          {children}
                          <Footer />
                        </GlobalImpressionProvider>
                      </ImpressionTrackingErrorBoundary>
                    </HeaderProvider>
                  </DialogProvider>
                </PriceTrackingProvider>
              </FavoritesProvider>
            </CurrencyProvider>
          </AuthProvider>
        </GoogleAuthProvider>
      </PostHogProvider>
    </ThemeProvider>
  </AppRouterCacheProvider>
);

export default Providers;
